import type { CollectionReference, DocumentData } from 'firebase/firestore'
import {
  collection,
  doc,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { modelItemStream, modelListStream } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { buildUnsupportedConverter } from '../shared/noops'
import type { FirestorePromptInteractiveElementType } from './schema'
import { schema } from './schema'
import { PromptInteractiveElementType } from '../../models/PromptInteractiveElementType'
import { type InteractiveElementPromptType } from './types'
import { updateDocWithError } from '../../firestore-mobx/fetch'

export * from './schema'

const unsupported =
  buildUnsupportedConverter<FirestorePromptInteractiveElementType>()

const readConverter: FirestoreDataConverter<FirestorePromptInteractiveElementType> =
  {
    toFirestore: unsupported.toFirestore,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

const getReadCollectionRef = (
  firestore: Firestore
): CollectionReference<FirestorePromptInteractiveElementType, DocumentData> => {
  return collection(
    firestore,
    'prompt',
    'interactive_element_prompt',
    'type'
  ).withConverter(readConverter)
}

export const getPromptInteractiveElementTypes = (
  repository: FirebaseRepository
) => {
  const ref = getReadCollectionRef(repository.firestore)
  return modelListStream(repository, ref, PromptInteractiveElementType)
}

export const getPromptInteractiveElementType = (
  repository: FirebaseRepository,
  { type }: { type: InteractiveElementPromptType }
) => {
  const col = getReadCollectionRef(repository.firestore)
  const docRef = doc(col, type)
  return modelItemStream(repository, docRef, PromptInteractiveElementType)
}

export const updatePromptInteractiveElementType = async (
  repository: FirebaseRepository,
  {
    title,
    description,
    interactiveElementType,
  }: Pick<FirestorePromptInteractiveElementType, 'title' | 'description'> & {
    interactiveElementType: InteractiveElementPromptType
  }
) => {
  const ref = getReadCollectionRef(repository.firestore)
  const docRef = doc(ref, interactiveElementType).withConverter(null)
  return await updateDocWithError(
    docRef,
    {
      title,
      description,
      updatedAt: serverTimestamp(),
    },
    'UpdatePromptInteractiveElementTypeError'
  )
}
